import { useEffect, useRef, useState } from "react"
import * as THREE from "three"
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js"
import { buildRocket, setRocketState } from "@/components/viewer/RocketModel"

interface TrajectoryPoint {
  t: number
  x: number
  z: number
  y: number
  speed: number
  fuel: number
  throttle: number
  altitude: number
  vx: number
  vy: number
  vz: number
  tilt: number
}

interface TrajectorySeries {
  points: TrajectoryPoint[]
  color: string
  label: string
}

interface Trajectory3DProps {
  points?: TrajectoryPoint[]
  series?: TrajectorySeries[]
  height?: number
  playing?: boolean
  onTelemetryUpdate?: (pt: TrajectoryPoint | null) => void
}

function normalizeSeries(
  points?: TrajectoryPoint[],
  series?: TrajectorySeries[],
): TrajectorySeries[] {
  if (series && series.length > 0) return series
  if (points && points.length > 0)
    return [{ points, color: "#22d3ee", label: "Flight telemetry" }]
  return []
}

function interpolatePoint(points: TrajectoryPoint[], t: number): TrajectoryPoint | null {
  if (points.length === 0) return null
  if (t <= points[0].t) return points[0]
  if (t >= points[points.length - 1].t) return points[points.length - 1]
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]
    const b = points[i + 1]
    if (t >= a.t && t <= b.t) {
      const k = (t - a.t) / (b.t - a.t)
      return {
        t,
        x: a.x + (b.x - a.x) * k,
        z: a.z + (b.z - a.z) * k,
        y: a.y + (b.y - a.y) * k,
        speed: a.speed + (b.speed - a.speed) * k,
        fuel: a.fuel + (b.fuel - a.fuel) * k,
        throttle: a.throttle + (b.throttle - a.throttle) * k,
        altitude: a.altitude + (b.altitude - a.altitude) * k,
        vx: a.vx + (b.vx - a.vx) * k,
        vy: a.vy + (b.vy - a.vy) * k,
        vz: a.vz + (b.vz - a.vz) * k,
        tilt: a.tilt + (b.tilt - a.tilt) * k,
      }
    }
  }
  return points[points.length - 1]
}

export function Trajectory3D({
  points,
  series,
  height = 420,
  playing = true,
  onTelemetryUpdate,
}: Trajectory3DProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [time, setTime] = useState(0)
  const allSeries = normalizeSeries(points, series)
  const rocketRefs = useRef<THREE.Group[]>([])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const scene = new THREE.Scene()
    scene.background = new THREE.Color(0x04060a)
    scene.fog = new THREE.FogExp2(0x04060a, 0.018)

    const camera = new THREE.PerspectiveCamera(45, el.clientWidth / el.clientHeight || 1, 0.1, 2000)
    camera.position.set(130, 130, 150)
    camera.lookAt(0, 0, 0)

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    })
    renderer.setSize(el.clientWidth, el.clientHeight)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
    renderer.setClearColor(0x04060a, 1)
    el.appendChild(renderer.domElement)

    const controls = new OrbitControls(camera, renderer.domElement)
    controls.enableDamping = true
    controls.dampingFactor = 0.05
    controls.minDistance = 8
    controls.maxDistance = 1200

    const ambient = new THREE.AmbientLight(0xffffff, 0.4)
    scene.add(ambient)

    const keyLight = new THREE.DirectionalLight(0xffffff, 0.9)
    keyLight.position.set(60, 120, 40)
    keyLight.castShadow = true
    keyLight.shadow.camera.left = -140
    keyLight.shadow.camera.right = 140
    keyLight.shadow.camera.top = 140
    keyLight.shadow.camera.bottom = -140
    keyLight.shadow.mapSize.width = 1024
    keyLight.shadow.mapSize.height = 1024
    scene.add(keyLight)

    const fill = new THREE.DirectionalLight(0x22d3ee, 0.2)
    fill.position.set(-60, 30, -80)
    scene.add(fill)

    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(1000, 1000, 64, 64),
      new THREE.MeshStandardMaterial({ color: 0x05080c, roughness: 0.95, metalness: 0.05 }),
    )
    ground.rotation.x = -Math.PI / 2
    ground.receiveShadow = true
    scene.add(ground)

    const grid = new THREE.GridHelper(1000, 50, 0x141c2a, 0x070b12)
    grid.position.z = 0.05
    scene.add(grid)

    const targetGroup = new THREE.Group()
    for (let i = 1; i <= 4; i++) {
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(i * 5 - 1, i * 5, 64),
        new THREE.MeshBasicMaterial({ color: 0xef4444, transparent: true, opacity: 0.2 - i * 0.04 }),
      )
      ring.rotation.x = -Math.PI / 2
      targetGroup.add(ring)
    }
    const cross = new THREE.Mesh(
      new THREE.BoxGeometry(70, 0.05, 70),
      new THREE.MeshBasicMaterial({ color: 0xef4444, transparent: true, opacity: 0.45 }),
    )
    cross.position.z = 0.1
    targetGroup.add(cross)
    scene.add(targetGroup)

    allSeries.forEach((s) => {
      if (s.points.length < 2) return

      const linePts = s.points.map((p) => new THREE.Vector3(p.x, p.z, Math.max(0, p.y)))
      const lineGeo = new THREE.BufferGeometry().setFromPoints(linePts)
      const lineMat = new THREE.LineBasicMaterial({
        color: new THREE.Color(s.color),
        transparent: true,
        opacity: 0.22,
      })
      scene.add(new THREE.Line(lineGeo, lineMat))

      const startMarker = new THREE.Mesh(
        new THREE.SphereGeometry(1.1, 16, 16),
        new THREE.MeshBasicMaterial({ color: 0x22d3ee }),
      )
      startMarker.position.set(s.points[0].x, s.points[0].z, s.points[0].y + 0.5)
      scene.add(startMarker)

      const last = s.points[s.points.length - 1]
      const landMarker = new THREE.Mesh(
        new THREE.SphereGeometry(1.1, 16, 16),
        new THREE.MeshBasicMaterial({ color: last.speed <= 10 ? 0x10b981 : 0xef4444 }),
      )
      landMarker.position.set(last.x, last.z, 0.6)
      scene.add(landMarker)

      const rocket = buildRocket(new THREE.Color(s.color).getHex())
      scene.add(rocket)
      rocketRefs.current.push(rocket)
    })

    const totalDuration =
      allSeries.reduce((max, s) => Math.max(max, s.points[s.points.length - 1]?.t ?? 0), 0) || 0

    let animId: number | null = null

    const renderFrame = () => {
      let t = time
      if (playing) {
        t = time + 1 / 60
        if (t > totalDuration) t = totalDuration
        setTime(t)
      }

      rocketRefs.current.forEach((rocket, si) => {
        const s = allSeries[si]
        if (!s || s.points.length === 0) return
        const pt = interpolatePoint(s.points, t)
        if (!pt) return
        setRocketState(rocket, {
          x: pt.x,
          z: pt.z,
          y: pt.y,
          pitch: (pt.vz / 100) * 0.6,
          yaw: (pt.vx / 100) * 0.6,
          roll: (pt.tilt / 180) * 0.5,
          throttle: pt.throttle,
        })
      })

      if (onTelemetryUpdate && allSeries[0]) {
        onTelemetryUpdate(interpolatePoint(allSeries[0].points, t))
      }

      controls.update()
      renderer.render(scene, camera)
    }

    renderFrame()
    animId = requestAnimationFrame(function frame() {
      renderFrame()
      animId = requestAnimationFrame(frame)
    })

    const handleResize = () => {
      if (!el) return
      camera.aspect = el.clientWidth / el.clientHeight
      camera.updateProjectionMatrix()
      renderer.setSize(el.clientWidth, el.clientHeight)
    }
    window.addEventListener("resize", handleResize)

    return () => {
      if (animId != null) cancelAnimationFrame(animId)
      window.removeEventListener("resize", handleResize)
      controls.dispose()
      renderer.dispose()
      scene.traverse((object) => {
        if ((object as THREE.Mesh).geometry) (object as THREE.Mesh).geometry.dispose()
        if ((object as THREE.Mesh).material) {
          const m = object as THREE.Mesh
          if (Array.isArray(m.material)) m.material.forEach((mat) => mat.dispose())
          else m.material.dispose()
        }
      })
      if (el && renderer.domElement.parentNode === el) el.removeChild(renderer.domElement)
    }
  }, [allSeries, playing, onTelemetryUpdate])

  if (allSeries.length === 0 || allSeries.every((s) => s.points.length === 0)) {
    return (
      <div ref={containerRef} className="w-full panel flex flex-col items-center justify-center" style={{ height }}>
        <div className="flex flex-col items-center gap-2">
          <div className="w-12 h-12 rounded border border-border flex items-center justify-center text-dim">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 19l7-7 7 7M5 12l7-7 7 7M12 4l-2 8M12 20l-2-8" />
            </svg>
          </div>
          <p className="text-sm text-muted">No trajectory data</p>
          <p className="text-[0.625rem] text-dim">Run a simulation to visualize the flight path</p>
        </div>
      </div>
    )
  }

  const latest = allSeries[0].points[allSeries[0].points.length - 1]

  return (
    <div ref={containerRef} className="w-full panel relative overflow-hidden flex" style={{ height }}>
      <div className="absolute inset-0 z-0" />
      <div className="absolute bottom-3 left-3 right-3 z-10 pointer-events-none">
        <div className="mx-auto bg-[#060b14]/80 backdrop-blur border border-border rounded-sm px-3 py-2 flex flex-wrap items-center justify-center gap-3 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-cyan-400" />
            <span className="text-cyan-300 mono">ALT {latest.altitude.toFixed(0)} m</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-blue-400" />
            <span className="text-blue-300 mono">SPD {latest.speed.toFixed(1)} m/s</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-amber-400" />
            <span className="text-amber-300 mono">FUEL {latest.fuel.toFixed(0)} kg</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-cyan-400/60" />
            <span className="text-cyan-300 mono">T+{latest.t.toFixed(0)}s</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[#22d3ee]" />
            <span className="text-cyan-300 mono">{latest.speed <= 10 ? "LANDED" : "IN FLIGHT"}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
